import * as React from 'react';

import '../Main.scss';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    Button,
    Col,
    Row
} from 'reactstrap';

import { ErrorState } from '../ErrorBanner/ErrorBanner';

import { SetSubmissionEmail } from '../../Services/SubmissionService';
import { ApplicationState } from '../../Store';
import {
    GetDefaultSubmissionOptions,
    GetMultipleBorrowerImportsAllowed,
    GetSupportEmail,
} from '../../Store/AppSettings';
import { BorrowerActionCreators, GetBorrowerAnonymousState } from '../../Store/Borrower';
import { actionCreators as ImportFinancialsActions } from '../../Store/ImportFinancials';
import { GetRequestedOptions } from '../../Store/NewDataCollectionParameters';
import { TestEmail } from '../../Utils/FormUtils';
import { ConfirmModal, ConfirmModalType } from '../ConfirmModal/ConfirmModal';

import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';

import { BorrowerPortalChildProps } from './BorrowerPortalV2';

import { AccountingPkgPresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';
import { NewImportWidget } from './NewImportWidget';
import { ReceiveEmailedDocumentWidget } from './ReceiveEmailedDocumentWidget';
import { UploadAdditionalFilesWidget } from './UploadAdditionalFilesWidget';

import { StringFormat } from '../../Utils/StringUtils';

import {
    PortalTextBorrowerCustomerCopyEmail,
    PortalTextBorrowerFileUpload,
    PortalTextBorrowerLinked,
    PortalTextCommon,
    PortalTextCommonImport,
} from '../../Models/Api/strongbox.financialportal';
import {
    GetPortalLanguage,
    GetPortalText,
} from '../../Store/PortalAssets';


type AdditionalBorrowerAction = {
    descriptionText: string;
    imageClass: string;
    disabled: boolean;
    renderIndex: number;
    selected: boolean;
    id: 'importAdditional' | 'uploadOtherDocs' | 'requestEmailedCopy';
};

type InjectedReduxState = {
    borrowerAnonymous: boolean;
    shareableLinkOptions: PortalCreateSubmissionRequestOptions | undefined;
    defaultAnonymousSubmissionOptions: PortalCreateSubmissionRequestOptions;
    allowMultipleBorrowerImports: boolean;
    commonText?: PortalTextCommon;
    borrowerLinkedText?: PortalTextBorrowerLinked;
    fileUploadText?: PortalTextBorrowerFileUpload;
    rcvCustomerCopyText?: PortalTextBorrowerCustomerCopyEmail;
    customerSupportEmail: string;
    portalLanguageCode: string;
};

type InjectedActionCreators = typeof BorrowerActionCreators & typeof ImportFinancialsActions;

type Props = BorrowerPortalChildProps & InjectedActionCreators & InjectedReduxState;

export const LinkedComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        borrowerId,
        defaultAnonymousSubmissionOptions,
        importAccountingPkg,
        shareableLinkOptions,
        SetBorrowerSubmissionId,
        submissionId,
        allowMultipleBorrowerImports,
        borrowerLinkedText,
        fileUploadText,
        rcvCustomerCopyText,
        customerSupportEmail,
        portalLanguageCode,
    } = props;

    const [borrowerEmailValid, setBorrowerEmailValid] = React.useState<boolean>(false);
    const [borrowerEmail, setBorrowerEmail] = React.useState<string>('');
    const [settingSubmissionEmail, setSettingSubmissionEmail] = React.useState<boolean>(false);
    const [submissionEmailError, setSubmissionEmailError] = React.useState<ErrorState | undefined>(undefined);
    const [currentEmailSubmitted, setCurrentEmailSubmitted] = React.useState<boolean>(false);
    const [showEmailConfirm, setShowEmailConfirm] = React.useState<string | undefined>(undefined);
    const [additionalBorrowerActions, setAdditionalBorrowerActions] = React.useState<AdditionalBorrowerAction[]>([]);
    const [activeBorrowerAction, setActiveBorrowerAction] = React.useState<AdditionalBorrowerAction | undefined>(undefined);
    const [disableBorrowerAction, setDisableBorrowerAction] = React.useState<boolean>(false);

    const [importWhenSubmissionIdClears, setImportWhenSubmissionIdClears] = React.useState<AccountingPkgPresentation | undefined>(undefined);

    React.useEffect(() => {
        props.ImportHasStarted();
        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const borrowerEmailChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newEmail = event.currentTarget.value.trimLeft();

        const valid = (newEmail !== '') && TestEmail(newEmail);

        setBorrowerEmail(newEmail);
        setBorrowerEmailValid(valid);
        setCurrentEmailSubmitted(false);
        setShowEmailConfirm(undefined);
    }

    const submitBorrowerEmail = async (): Promise<void> => {
        // This is a total sanity check.  The button shouldn't be enabled if this is not true
        // so we should never arrive here if it's false.
        if (!borrowerEmailValid) {
            return;
        }

        setSettingSubmissionEmail(true);
        setDisableBorrowerAction(true);

        try {
            await SetSubmissionEmail(
                props.borrowerId,
                props.submissionId,
                {
                    emailAddress: borrowerEmail,
                    supportEmailAddress: customerSupportEmail,
                    userLanguage: portalLanguageCode
                }
            );

            setSettingSubmissionEmail(false);
            setSubmissionEmailError(undefined);
            setCurrentEmailSubmitted(true);
            setShowEmailConfirm(borrowerEmail);
        } catch (exception) {
            setSettingSubmissionEmail(false);
            setSubmissionEmailError({
                summaryMessage: borrowerLinkedText?.borrowerSubmissionError || '',
                extraInformation: exception.toString(),
            });
            setCurrentEmailSubmitted(false);
        } finally {
            setDisableBorrowerAction(false);
        }

    }

    const renderEmailDocWidget = (): React.ReactElement => {
        return (
            <ReceiveEmailedDocumentWidget
                borrowerEmail={borrowerEmail}
                borrowerEmailChanged={borrowerEmailChanged}
                disabled={!borrowerEmailValid || settingSubmissionEmail || currentEmailSubmitted}
                submitBorrowerEmail={submitBorrowerEmail}
                errorMsg={submissionEmailError}
                rcvCustomerCopyText={rcvCustomerCopyText}
            />
        );
    }

    const renderUploadFilesWidget = (): React.ReactElement => {
        return (
            <UploadAdditionalFilesWidget
                borrowerId={borrowerId}
                submissionId={submissionId}
                onWorking={(working) => setDisableBorrowerAction(working)}
                fileUploadText={fileUploadText}
            />
        )
    }

    React.useEffect(() => {
        if ((!submissionId) && (!!importWhenSubmissionIdClears)) {
            // Shouldn't have to worry about anonymous flow here.  To get past the initial screen the 
            // user had to submit a workspace name and email address at which point a workspace was
            // created and the ID for that workspace is in the store. We pass true in the second parameter
            // to override the default check for it not being an anonymous submission.
            importAccountingPkg(importWhenSubmissionIdClears, true);

            setImportWhenSubmissionIdClears(undefined);
        }

        // importAccountingPkg and setImportWhenSubmissionIdClears don't need to be in the dependency list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importWhenSubmissionIdClears, submissionId])

    const renderNewImportWidget = (): React.ReactElement => {
        return (
            <NewImportWidget
                borrowerPortalProps={props}
                importFinancials={(pkg) => {
                    // Clear out the existing submission id so we go forward with a new one.
                    SetBorrowerSubmissionId('');

                    // We need the submissionId to actually clear so we go into a temporary state where
                    // we're waiting for it to clear at which point we'll run a new import
                    setImportWhenSubmissionIdClears(pkg);
                }}
                tabIndexBase={additionalBorrowerActions.length + 1}
            />
        )
    }

    // This is a little weird. Due to the vagaries of the way 'const' things work during a render
    // having the activeBorrowerAction contain a function or lambda doesn't really work since
    // it doesn't get changed.  Rather, we create this array that references the methods and define
    // the index each of them lives at. 
    //
    // When we build the array of actionable items, we reference the index constants that reliably
    // map back to the correct method. Then during render, if there's an active action, we use its
    // index into the widgetRenders array to render the content.  This keeps everything up to date
    // and working well.

    const widgetRenderers = [
        renderUploadFilesWidget,
        renderEmailDocWidget,
        renderNewImportWidget
    ];

    const uploadFilesWidgetIndex = 0;
    const emailDocWidgetIndex = 1;
    const newImportWidgetIndex = 2;

    React.useEffect(() => {
        const newActions: AdditionalBorrowerAction[] = [];

        // We used to check to ensure that the disableFileUpload flag was not enabled, however,
        // it is conceivable and configuration supports that the tenant could choose to disable
        // the ability to CHANGE the allow additional file upload setting while also setting the
        // default value to true.   
        //
        // So the ability to see and change the setting is untethered from what the setting 
        // actually is.

        if (
            (((!shareableLinkOptions) && (defaultAnonymousSubmissionOptions.allowUserUpload)) ||
                ((!!shareableLinkOptions) && (shareableLinkOptions.allowUserUpload))) &&
            (!!borrowerId)
        ) {
            newActions.push({
                descriptionText: borrowerLinkedText?.uploadAdditionalDocuments || '',
                imageClass: 'upload-additional-image',
                disabled: disableBorrowerAction,
                renderIndex: uploadFilesWidgetIndex,
                selected: !!activeBorrowerAction && activeBorrowerAction.renderIndex === uploadFilesWidgetIndex,
                id: 'uploadOtherDocs'
            });
        }

        if (
            ((!shareableLinkOptions && defaultAnonymousSubmissionOptions.provideUserCopy) ||
                (!!shareableLinkOptions && shareableLinkOptions.provideUserCopy)) &&
            (!!borrowerId) &&
            (!!SetBorrowerSubmissionId)
        ) {
            newActions.push({
                descriptionText: borrowerLinkedText?.emailYourselfDocs || '',
                imageClass: 'send-email-image',
                disabled: disableBorrowerAction,
                renderIndex: emailDocWidgetIndex,
                selected: !!activeBorrowerAction && activeBorrowerAction.renderIndex === emailDocWidgetIndex,
                id: 'requestEmailedCopy'
            });
        }

        if (allowMultipleBorrowerImports) {
            newActions.push({
                descriptionText: borrowerLinkedText?.importAdditionalFinancials || '',
                imageClass: 'repeat-import-image',
                disabled: disableBorrowerAction,
                renderIndex: newImportWidgetIndex,
                selected: !!activeBorrowerAction && activeBorrowerAction.renderIndex === newImportWidgetIndex,
                id: 'importAdditional'
            })
        }

        setAdditionalBorrowerActions(newActions);

        // I want this to execute equivalent to componentDidMount so this is appropriate, 
        // having the actual dependencies results in circular rendering and an infinite loop
        // Except for disableBorrowerAction and activeBorrowerAction...then things are broken 
        // if we don't rerender on that dependency changing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBorrowerAction, disableBorrowerAction]);

    const mainMessage = React.useMemo(() => {
        if (additionalBorrowerActions.length === 0) {
            return borrowerLinkedText?.linkCompleteNoActions || '';
        }
        if (additionalBorrowerActions.length > 1) {
            return borrowerLinkedText?.linkComplete || '';
        }
        // At this point the list of actions has a length of 1.  
        return !!additionalBorrowerActions.find(action => action.id === 'importAdditional') ? (borrowerLinkedText?.linkCompleteImportOnly || '') : (borrowerLinkedText?.linkComplete || '');

        // This is the actual list of dependencies I want
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [additionalBorrowerActions]);

    const receiveEmailShortlyAt = React.useMemo(() => {
        return StringFormat(borrowerLinkedText?.templateYouWillReceiveEmailShortly || '{0}', borrowerEmail);
    }, [borrowerEmail, borrowerLinkedText])

    return (
        <React.Fragment>
            {!activeBorrowerAction && (
                <>
                    <Row>
                        <Col xs={12} className={'centered-col'}>
                            <h1>{borrowerLinkedText?.collectionCompleted || ''}</h1>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} className={'centered-col'}>
                            <p>{mainMessage}</p>
                        </Col>
                    </Row>
                </>
            )}
            {props.borrowerAnonymous && (
                <Row>
                    <Col xs={12} className={'centered-col'}>
                        <h2 className={'normal leading-paragraph-text-grouping'}>{borrowerLinkedText?.pleaseCheckInbox || ''}</h2>
                    </Col>
                </Row>
            )}
            <Row className={'basic-height-separator'} />
            {
                additionalBorrowerActions.map((borrowerAction, index) => {
                    return (
                        <Row
                            key={`additional-borrower-actions-row-${index}`}
                            style={{
                                marginBottom: '15px',
                                width: '100%'
                            }}
                        >
                            <Col
                                key={`additional-borrower-actions-col-${index}`}
                            >
                                <Button
                                    onClick={() => setActiveBorrowerAction(borrowerAction)}
                                    disabled={borrowerAction.disabled}
                                    tabIndex={index + 1}
                                    autoFocus={index === 0}
                                    color="secondary"
                                    className={'additional-action'}
                                >
                                    {borrowerAction.descriptionText}
                                </Button>
                            </Col>
                        </Row>

                    )
                })
            }
            {
                !!activeBorrowerAction && (
                    <React.Fragment>
                        <Row className={'active-action-container'}>
                            <hr />
                            <Col>
                                {widgetRenderers[activeBorrowerAction.renderIndex]()}
                            </Col>
                        </Row>
                    </React.Fragment>
                )
            }
            {showEmailConfirm && (
                <ConfirmModal
                    msg={receiveEmailShortlyAt}
                    onTerminalButton={(yes?: boolean) => {
                        setShowEmailConfirm(undefined);
                    }}
                    title={borrowerLinkedText?.emailSubmitted || ''}
                    modalType={ConfirmModalType.ok}
                    commonText={props.commonText}
                />
            )}
        </React.Fragment>
    );
}

export const Linked = connect<InjectedReduxState, InjectedActionCreators, BorrowerPortalChildProps, ApplicationState>(
    (appState: ApplicationState) => {
        const commonText = GetPortalText<PortalTextCommon>(appState, 'commonText');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        const result: InjectedReduxState = {
            borrowerAnonymous: GetBorrowerAnonymousState(appState),
            shareableLinkOptions: GetRequestedOptions(appState),
            defaultAnonymousSubmissionOptions: GetDefaultSubmissionOptions(appState, 'shareableanonymous', true, commonImportText),
            allowMultipleBorrowerImports: GetMultipleBorrowerImportsAllowed(appState),
            commonText,
            borrowerLinkedText: GetPortalText<PortalTextBorrowerLinked>(appState, 'borrowerLinked'),
            fileUploadText: GetPortalText<PortalTextBorrowerFileUpload>(appState, 'borrowerFileUpload'),
            rcvCustomerCopyText: GetPortalText<PortalTextBorrowerCustomerCopyEmail>(appState, 'borrowerCustomerCopyEmail'),
            customerSupportEmail: GetSupportEmail(appState),
            portalLanguageCode: GetPortalLanguage(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...BorrowerActionCreators,
            ...ImportFinancialsActions,
        },
        dispatch
    )
)(LinkedComponent);

